/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2024 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2024 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */
import { Box } from '@mui/material';
import LinearProgress from '@mui/material/LinearProgress';
import type { FC } from 'react';
import { useState, useEffect } from 'react';
import { AlertBanner, dateUtils } from '@opengeoweb/shared';
import type { TafModuleProviderProps } from './TafModuleProvider';
import type { ForecastType } from '../../types';
import { useTafTranslation } from '../../utils/i18n';
import { useTafList } from '../../utils/taf-api/hooks';
import { ResponsiveWrapper } from '../ResponsiveWrapper/ResponsiveWrapper';
import TafLayout from '../TafLayout/TafLayout';
import { TafWrapper } from '../TafWrapper';
import TafConfigWrapper from '../TafWrapper/TafConfigWrapper';

export const getCurrentDate = (): string =>
  dateUtils.dateToString(dateUtils.utc(), dateUtils.DATE_FORMAT_HOURS)!;

const TafModule: FC<{
  isAutoTafEnabled: boolean;
  forecastType: ForecastType;
}> = ({ isAutoTafEnabled, forecastType }) => {
  const { t } = useTafTranslation();
  const [errorMessage, setErrorMessage] = useState<{
    title: string;
    info?: string;
  } | null>(null);
  const {
    isFetching,
    error: tafListError,
    data: tafList,
    dataUpdatedAt,
    refetch: fetchNewTafList,
  } = useTafList(forecastType);

  const lastUpdateTime = dateUtils.dateToString(
    new Date(dataUpdatedAt),
    dateUtils.DATE_FORMAT_HOURS,
  );

  const onUpdateTaf = async (): Promise<void> => {
    await fetchNewTafList();
  };

  useEffect(() => {
    if (tafListError) {
      setErrorMessage({
        title: t('error-list'),
        info: tafListError.message,
      });
    } else {
      setErrorMessage(null);
    }
  }, [t, tafListError]);

  return (
    <ResponsiveWrapper>
      <Box
        component="div"
        data-testid="tafmodule"
        id="tafmodule"
        sx={{
          height: '100%',
        }}
      >
        {isFetching ? (
          <LinearProgress
            color="secondary"
            data-testid="loading-bar"
            sx={{ marginBottom: '-4px' }}
          />
        ) : null}
        {errorMessage ? (
          <AlertBanner
            actionButtonProps={{
              title: t('error-title-retry'),
              onClick: onUpdateTaf,
            }}
            data-testid="error-message"
            info={errorMessage.info}
            severity="error"
            title={errorMessage.title}
          />
        ) : null}
        <TafLayout
          isAutoTafEnabled={isAutoTafEnabled}
          lastUpdateTime={lastUpdateTime}
          onUpdateTaf={onUpdateTaf}
          tafList={tafList || []}
        />
      </Box>
    </ResponsiveWrapper>
  );
};

const Wrapper: FC<
  TafModuleProviderProps & {
    isAutoTafEnabled?: boolean;
    forecastType?: ForecastType;
  }
> = ({ isAutoTafEnabled = false, forecastType = 'TAF', ...props }) => {
  return (
    <TafWrapper {...props}>
      <TafConfigWrapper forecastType={forecastType}>
        <TafModule
          forecastType={forecastType}
          isAutoTafEnabled={isAutoTafEnabled}
        />
      </TafConfigWrapper>
    </TafWrapper>
  );
};

export default Wrapper;
